/**
 * Marketing Crew Service
 * Handles AI agent workflows including market research, content production, and campaign optimization
 */
import api from './api';

const marketingCrewService = {
  // ==================== Crew Status & Agents ====================
  /**
   * Get available crew agents
   */
  getAgents: async () => {
    const response = await api.get('/marketing/crew/agents');
    return response.data;
  },

  /**
   * Get crew status
   */
  getStatus: async () => {
    const response = await api.get('/marketing/crew/status');
    return response.data;
  },

  /**
   * Get agent details
   */
  getAgentDetails: async (agentId) => {
    const response = await api.get(`/marketing/crew/agents/${agentId}`);
    return response.data;
  },

  // ==================== Market Research Workflow ====================
  /**
   * Execute market research workflow
   */
  executeMarketResearch: async (config) => {
    const response = await api.post('/marketing/crew/workflows/market-research', config);
    return response.data;
  },

  // ==================== Content Production Workflow ====================
  /**
   * Execute content production workflow
   */
  executeContentProduction: async (config) => {
    const response = await api.post('/marketing/crew/workflows/content-production', config);
    return response.data;
  },

  // ==================== Campaign Optimization Workflow ====================
  /**
   * Execute campaign optimization workflow
   */
  executeCampaignOptimization: async (config) => {
    const response = await api.post('/marketing/crew/workflows/campaign-optimization', config);
    return response.data;
  },

  // ==================== Workflow Status ====================
  /**
   * Get workflow status
   */
  getWorkflowStatus: async (workflowId) => {
    const response = await api.get(`/marketing/crew/workflows/${workflowId}/status`);
    return response.data;
  },

  /**
   * Cancel a workflow
   */
  cancelWorkflow: async (workflowId) => {
    const response = await api.post(`/marketing/crew/workflows/${workflowId}/cancel`);
    return response.data;
  },

  /**
   * Get workflow results
   */
  getWorkflowResults: async (workflowId) => {
    const response = await api.get(`/marketing/crew/workflows/${workflowId}/results`);
    return response.data;
  },

  /**
   * Get all workflows history
   */
  getWorkflowHistory: async (params = {}) => {
    const response = await api.get('/marketing/crew/workflows', { params });
    return response.data;
  },

  // ==================== Audience Analysis ====================
  /**
   * Analyze demographics
   */
  analyzeDemographics: async (data) => {
    const response = await api.post('/marketing/audience/demographics', data);
    return response.data;
  },

  /**
   * Create psychographic profile
   */
  createPsychographicProfile: async (data) => {
    const response = await api.post('/marketing/audience/psychographics', data);
    return response.data;
  },

  /**
   * Generate customer personas
   */
  generatePersonas: async (data) => {
    const response = await api.post('/marketing/audience/personas', data);
    return response.data;
  },

  /**
   * Map customer journey
   */
  mapCustomerJourney: async (data) => {
    const response = await api.post('/marketing/audience/journey-map', data);
    return response.data;
  },

  /**
   * Generate comprehensive audience report
   */
  generateAudienceReport: async (data) => {
    const response = await api.post('/marketing/audience/comprehensive-report', data);
    return response.data;
  },

  // ==================== Behavioral Analytics ====================
  /**
   * Analyze purchase patterns
   */
  analyzePurchasePatterns: async (data) => {
    const response = await api.post('/marketing/behavioral/purchase-patterns', data);
    return response.data;
  },

  /**
   * Analyze engagement patterns
   */
  analyzeEngagement: async (data) => {
    const response = await api.post('/marketing/behavioral/engagement-analysis', data);
    return response.data;
  },

  /**
   * Profile decision making
   */
  profileDecisionMaking: async (data) => {
    const response = await api.post('/marketing/behavioral/decision-profiling', data);
    return response.data;
  },

  /**
   * Segment customers by behavior
   */
  segmentCustomers: async (data) => {
    const response = await api.post('/marketing/behavioral/customer-segmentation', data);
    return response.data;
  },

  /**
   * Predict customer behavior
   */
  predictBehavior: async (data) => {
    const response = await api.post('/marketing/behavioral/predictions', data);
    return response.data;
  },

  // ==================== SEO Services ====================
  /**
   * Analyze content for SEO
   */
  analyzeSEO: async (data) => {
    const response = await api.post('/marketing/seo/analyze', data);
    return response.data;
  },

  /**
   * Research keywords
   */
  researchKeywords: async (data) => {
    const response = await api.post('/marketing/seo/keywords/research', data);
    return response.data;
  },

  /**
   * Optimize content for SEO
   */
  optimizeForSEO: async (data) => {
    const response = await api.post('/marketing/seo/optimize', data);
    return response.data;
  }
};

export default marketingCrewService;
